import { matterScopeById } from "@/shared/utils/matterScope";
import { useEffect, useMemo, useState } from 'react'
import { Info, Search } from 'lucide-react'
import { ComboBox } from '@/shared/components/ComboBox/ComboBox'
import { Input } from '@/shared/components/ui/input'
import { useRestrictedWords } from '@/shared/cache'
import { useMattersCache } from '@/shared/cache/slices/matters'
import type { RestrictedWord } from '@/shared/cache/slices/restrictedWords'
import type { Matter } from '@/shared/cache/slices/matters'
import type { SearchableField } from '@/shared/utils/smartSearch'
import { renderMatterItem, renderSelectedMatter } from '../v2/pickerItems'

// ── Helpers ────────────────────────────────────────────────────────

function SeverityDot({ blocked }: { blocked: boolean }) {
	return (
		<span
			className={`inline-block h-2 w-2 rounded-full shrink-0 mt-1 ${
				blocked ? 'bg-red-500' : 'bg-amber-500'
			}`}
			title={blocked ? 'Blocked' : 'Warning'}
		/>
	)
}

function RuleRow({ rule }: { rule: RestrictedWord }) {
	return (
		<div className="flex gap-2.5 px-3 py-2.5 border-b border-border/30 last:border-b-0">
			<SeverityDot blocked={rule.severity === 'block'} />
			<div className="flex flex-col gap-0.5 min-w-0">
				<span className="text-sm text-foreground font-medium">{rule.displayName}</span>
				<span className="text-xs text-muted-foreground">
					{rule.suggestion ? (
						<>Suggested replacement: <span className="font-medium text-foreground">{rule.suggestion}</span></>
					) : (
						'No replacement suggested'
					)}
				</span>
			</div>
		</div>
	)
}

function RuleSection({
	title,
	rules,
	variant,
}: {
	title: string
	rules: RestrictedWord[]
	variant: 'blocked' | 'warning'
}) {
	if (rules.length === 0) return null

	return (
		<div>
			<h3
				className={`text-xs font-medium uppercase tracking-wide mb-1.5 ${
					variant === 'blocked'
						? 'text-red-600 dark:text-red-400'
						: 'text-amber-600 dark:text-amber-400'
				}`}
			>
				{title} ({rules.length})
			</h3>
			<div className="rounded-md border border-border/50">
				{rules.map((rule, index) => (
					<RuleRow key={`${rule.word}-${index}`} rule={rule} />
				))}
			</div>
		</div>
	)
}

// ── Component ──────────────────────────────────────────────────────

export default function RestrictedWordsTab() {
	const { getRestrictedWords, fetchAllForClient } = useRestrictedWords()
	const firmWideWords = getRestrictedWords()
	const { matters } = useMattersCache()

	// Selected matter state
	const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null)
	const [scopedRules, setScopedRules] = useState<RestrictedWord[] | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [wordSearch, setWordSearch] = useState('')

	// Use scoped rules if a matter is selected, otherwise firm-wide
	const rules = selectedMatterId ? (scopedRules ?? []) : firmWideWords

	// Find selected matter for display
	const selectedMatter = useMemo(
		() => matters.find((m) => m.matter_id === selectedMatterId) || null,
		[matters, selectedMatterId]
	)

	// Fetch rules when matter selection changes
	useEffect(() => {
		if (selectedMatterId) {
			const scope = matterScopeById(selectedMatterId, matters);
			const clientCode = scope?.clientCode ?? "";
			const matterCode = scope?.matterId;
			let cancelled = false
			setIsLoading(true)
			setScopedRules(null)
			fetchAllForClient(clientCode, matterCode)
				.then((rules) => {
					if (!cancelled) setScopedRules(rules)
				})
				.catch(() => {
					if (!cancelled) setScopedRules([])
				})
				.finally(() => {
					if (!cancelled) setIsLoading(false)
				})
			return () => {
				cancelled = true
			}
		} else {
			setScopedRules(null)
			setIsLoading(false)
		}
	}, [selectedMatterId, matters, fetchAllForClient])

	// Filter rules by word search
	const filteredRules = useMemo(() => {
		const query = wordSearch.trim().toLowerCase()
		if (!query) return rules
		return rules.filter((r) =>
			r.displayName.toLowerCase().includes(query) ||
			(r.suggestion?.toLowerCase().includes(query) ?? false)
		)
	}, [rules, wordSearch])

	// Group rules by severity
	const blocked = useMemo(() => filteredRules.filter((r) => r.severity === 'block'), [filteredRules])
	const warnings = useMemo(() => filteredRules.filter((r) => r.severity === 'warning'), [filteredRules])

	// ComboBox field extractors — matches EntryForm.tsx
	const matterFields: ((item: Matter) => SearchableField)[] = useMemo(
		() => [
			(m: Matter) => ({ value: m.matter, weight: 1.5 }),
			(m: Matter) => ({ value: m.client, weight: 1.2 }),
			(m: Matter) => ({ value: m.description, weight: 1.0 }),
		],
		[]
	)

	const handleMatterChange = (matterId: string) => {
		setSelectedMatterId(matterId || null)
	}

	const handleClear = () => {
		setSelectedMatterId(null)
	}

	return (
		<div className="flex flex-col gap-3">
			{/* Matter Filter */}
			<div>
				<label className="text-xs font-medium text-muted-foreground mb-1 block">
					Client / Matter
				</label>
				<ComboBox
					items={matters}
					value={selectedMatterId}
					onChange={handleMatterChange}
					fields={matterFields}
					getKey={(m) => m.matter_id}
					getDisplayLabel={(m) => `${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`}
					renderItem={renderMatterItem}
					renderSelected={renderSelectedMatter}
					placeholder="All clients (firm-wide)"
					clearable
					onClear={handleClear}
				/>
			</div>

			{/* Word Search */}
			<div>
				<label className="text-xs font-medium text-muted-foreground mb-1 block">
					Search words
				</label>
				<div className="relative">
					<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						value={wordSearch}
						onChange={(e) => setWordSearch(e.target.value)}
						placeholder="Search restricted words..."
						className="pl-8 h-9 text-sm"
					/>
				</div>
			</div>

			{/* Info Banner - only show when matter selected or filtering */}
			{(selectedMatter || wordSearch) && (
				<div className="flex items-start gap-2 rounded-md border border-border/40 bg-muted/30 px-3 py-2">
					<Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
					<span className="text-xs text-muted-foreground">
						{selectedMatter && (
							<>
								Showing rules for <span className="font-medium text-foreground">{selectedMatter.client}</span> — {selectedMatter.description} (includes firm-wide rules).
							</>
						)}
						{wordSearch && (
							<>
								{selectedMatter && ' '}Filtered to {filteredRules.length} of {rules.length} rules.
							</>
						)}
					</span>
				</div>
			)}

			{/* Loading State */}
			{isLoading && (
				<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-6 text-center">
					<p className="text-sm text-muted-foreground">Loading rules...</p>
				</div>
			)}

			{/* Rules Content */}
			{!isLoading && rules.length === 0 ? (
				<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">
						No restricted words configured.
					</p>
				</div>
			) : !isLoading && filteredRules.length === 0 ? (
				<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-6 text-center">
					<p className="text-sm text-muted-foreground">
						No restricted words match "{wordSearch}"
					</p>
				</div>
			) : !isLoading && (
				<div className="flex flex-col gap-4 max-h-[calc(100vh-24rem)] overflow-y-auto pb-4">
					<RuleSection title="Blocked" rules={blocked} variant="blocked" />
					<RuleSection title="Warnings" rules={warnings} variant="warning" />
				</div>
			)}
		</div>
	)
}
